/**
 * Which doses are due on a day, for reminders (mobile app) and the Daily log
 * (website). Built from the same medicine fields as the purchase formulas in
 * index.ts, so "what you take" and "what you buy" always agree; see
 * test/doses.test.ts.
 *
 * Times are "HH:MM" wall-clock times on the person's phone. Dates are "YYYY-MM-DD".
 */
import { addDays, computeRangeNeed, courseDosesOn, courseInfo, fmtNum, formatDateISO, getEffectiveEndDate, parseDateISO, type Medicine, type Person } from './index';

export interface DoseTimes {
  morning: string;
  lunch: string;
  dinner: string;
  /** "Before meal" doses ring this many minutes before the meal time. */
  before_meal_minutes: number;
}
export const DEFAULT_DOSE_TIMES: DoseTimes = { morning: '08:00', lunch: '13:30', dinner: '20:30', before_meal_minutes: 30 };

/** When a dialysis-session medicine is reminded, by the person's dialysis time. */
export const DIALYSIS_SESSION_TIMES: Record<string, string> = { morning: '07:30', noon: '12:00', evening: '17:00' };

export type DoseSlot =
  | 'morning_before' | 'morning_after' | 'lunch_before' | 'lunch_after' | 'dinner_before' | 'dinner_after'
  | 'interval' | 'dialysis' | 'injection'
  /** dose n of a course (antibiotic etc.), e.g. 'course_7' */
  | `course_${number}`;

export const MEAL_SLOTS: DoseSlot[] = ['morning_before', 'morning_after', 'lunch_before', 'lunch_after', 'dinner_before', 'dinner_after'];

export const SLOT_LABELS: Record<Exclude<DoseSlot, `course_${number}`>, string> = {
  morning_before: 'Before breakfast', morning_after: 'After breakfast',
  lunch_before: 'Before lunch', lunch_after: 'After lunch',
  dinner_before: 'Before dinner', dinner_after: 'After dinner',
  interval: 'Every few days', dialysis: 'Dialysis day', injection: 'Injection',
};

/** An injection reminder from the Medicine Reminder page (reminders table). */
export interface InjectionReminder {
  id: string;
  name: string;
  dose?: string;
  time?: string;
  start_date?: string;
  interval_days?: number | string;
  active?: boolean;
  /** "🔔 I need a reminder for this injection" (default on). Off: no alert, not on the phone list, never "missed". */
  remind?: boolean;
  skipped_dates?: string[];
  /** Optional link to a medicine, so marking it Taken lowers that medicine's stock. */
  medicine_id?: string;
  [extra: string]: unknown;
}

export interface PlannedDose {
  /** `${sourceId}|${date}|${slot}`: the same key as dose_logs.id */
  key: string;
  source: 'medicine' | 'injection';
  /** medicine id, or reminder id for injections */
  sourceId: string;
  /** the medicine whose stock goes down (for injections: the linked medicine, if any) */
  medicineId: string | null;
  name: string;
  strength: string;
  type: string;
  slot: DoseSlot;
  date: string;
  time: string;
  amount: number;
  /** "1 tablet", "½", "10 units" — for display */
  amountLabel: string;
  label: string;
  /** For a course: which dose this is ("dose 7 of 40"). */
  course?: { n: number; total: number };
}

export interface DoseGroup { time: string; date: string; label: string; doses: PlannedDose[] }

// ---------------- helpers ----------------
const num = (v: unknown) => { const n = parseFloat(String(v ?? '')); return Number.isFinite(n) ? n : 0; };

export function addMinutes(hhmm: string, minutes: number): string {
  const [h, m] = (hhmm || '00:00').split(':').map(Number);
  let t = (h || 0) * 60 + (m || 0) + minutes;
  t = ((t % 1440) + 1440) % 1440;
  return `${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`;
}

/** "13:30" -> "1:30 PM" */
export function formatTime12(hhmm: string): string {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  return `${h % 12 || 12}:${String(m || 0).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
}

export function doseTimesFor(person: Person | null | undefined): DoseTimes {
  const t = (person && (person as { dose_times?: Partial<DoseTimes> }).dose_times) || {};
  const ok = (s: unknown, d: string) => (typeof s === 'string' && /^\d{2}:\d{2}$/.test(s) ? s : d);
  return {
    morning: ok(t.morning, DEFAULT_DOSE_TIMES.morning),
    lunch: ok(t.lunch, DEFAULT_DOSE_TIMES.lunch),
    dinner: ok(t.dinner, DEFAULT_DOSE_TIMES.dinner),
    before_meal_minutes: Number.isFinite(Number(t.before_meal_minutes)) ? Math.max(0, Math.min(120, Number(t.before_meal_minutes))) : DEFAULT_DOSE_TIMES.before_meal_minutes,
  };
}

export function slotTime(slot: DoseSlot, times: DoseTimes): string {
  const meal = slot.startsWith('morning') ? times.morning : slot.startsWith('lunch') ? times.lunch : times.dinner;
  return slot.endsWith('_before') ? addMinutes(meal, -times.before_meal_minutes) : meal;
}

/** Is the medicine's course running on this date? */
export function medicineActiveOn(med: Medicine, dateISO: string): boolean {
  if (med.start_date && dateISO < med.start_date) return false;
  const end = getEffectiveEndDate(med);
  if (end && dateISO > end) return false;
  return true;
}

function isDialysisDay(person: Person | null | undefined, dateISO: string): boolean {
  const days = person && Array.isArray(person.dialysis_days) ? person.dialysis_days.map(Number) : [];
  const d = parseDateISO(dateISO);
  return !!d && days.includes(d.getUTCDay());
}

function unitWord(typeId: string, amount: number): string {
  const t = String(typeId || '').toLowerCase();
  const many = amount > 1;
  if (/tab/.test(t)) return many ? 'tablets' : 'tablet';
  if (/cap/.test(t)) return many ? 'capsules' : 'capsule';
  if (/inj|insulin|vial|amp/.test(t)) return many ? 'injections' : 'injection';
  if (/syr|liq|susp/.test(t)) return 'ml';
  if (/drop/.test(t)) return many ? 'drops' : 'drop';
  if (/puff|inhal/.test(t)) return many ? 'puffs' : 'puff';
  if (/sach/.test(t)) return many ? 'sachets' : 'sachet';
  return '';
}

export function amountLabel(amount: number, typeId: string): string {
  const w = unitWord(typeId, amount);
  return w ? `${fmtNum(amount)} ${w}` : `× ${fmtNum(amount)}`;
}

/** The medicine an injection reminder is about: its medicine_id, or a medicine with the same name. */
export function linkedMedicine(rem: InjectionReminder, meds: Medicine[]): Medicine | null {
  if (rem.medicine_id) return meds.find(m => m.id === rem.medicine_id) || null;
  const n = String(rem.name || '').trim().toLowerCase();
  if (!n) return null;
  return meds.find(m => String(m.name || '').trim().toLowerCase() === n)
    || meds.find(m => { const mn = String(m.name || '').trim().toLowerCase(); return mn.length >= 3 && n.startsWith(mn + ' '); })
    || null;
}

/** Injection types (Injection, Insulin, IV / Infusion …). */
export function isInjectionType(typeId: unknown): boolean {
  return /inj|insulin|vial|amp|infus|^iv$/i.test(String(typeId || '').trim());
}

/**
 * "🔔 I need a reminder for this injection" — on unless the family unticked it.
 * Only injections can be switched off (an injection reminder, or a medicine whose type is an injection).
 */
export function wantsReminder(x: { remind?: unknown; type?: unknown } | null | undefined, isReminder = false): boolean {
  if (!x || x.remind !== false) return true;
  return !(isReminder || isInjectionType(x.type));
}

/** Doses of an injection reminder on a date ("Every N days from start"). */
export function injectionDueOn(rem: InjectionReminder, dateISO: string): boolean {
  if (rem.active === false || !rem.start_date) return false;
  const start = parseDateISO(rem.start_date), d = parseDateISO(dateISO);
  if (!start || !d || d < start) return false;
  const interval = Math.max(1, parseInt(String(rem.interval_days ?? 7), 10) || 7);
  if ((rem.skipped_dates || []).includes(dateISO)) return false;
  return Math.round((d.getTime() - start.getTime()) / 86400000) % interval === 0;
}

/**
 * Every dose planned for one person on one date, sorted by time.
 *
 * * daily medicines: one dose per non-zero slot (before / after breakfast, lunch, dinner)
 * * "Do not take on dialysis day": on dialysis days the medicine is not
 *   reminded at all ("Skips this medicine on dialysis days"). The purchase
 *   formula only subtracts the dose at the dialysis session, so it may buy
 *   a little more than is taken; that is the safe side.
 * * every-N-days medicines: on the days counted from the start date, at breakfast
 * * dialysis-session medicines ("On dialysis days only") are given at the
 *   dialysis centre: never reminded or counted here (see dialysisMedsOn)
 * * "fixed quantity" medicines have no set times: no reminder
 * * courses (antibiotics etc.): every 24/N hours from the first dose's date and time,
 *   until the total number of doses; times never move (a skipped dose still counts)
 * * injection reminders: at their own time. When a reminder is linked to a
 *   medicine (by id or the same name), that medicine's own doses that day are
 *   left out, so the same injection isn't reminded twice.
 * * injections with "🔔 I need a reminder" unticked (reminder or medicine): left out
 */
export function dosesForDay(
  person: Person | null | undefined,
  meds: Medicine[],
  reminders: InjectionReminder[],
  dateISO: string,
): PlannedDose[] {
  const times = doseTimesFor(person);
  const out: PlannedDose[] = [];
  const coveredByInjection = new Set<string>();

  for (const rem of reminders || []) {
    if (!injectionDueOn(rem, dateISO)) continue;
    const med = linkedMedicine(rem, meds);
    if (med) coveredByInjection.add(med.id);
    // An injection reminder for a "dialysis days only" medicine: given at the dialysis
    // centre, so no reminder on the phone and not counted (dialysisMedsOn lists it).
    if (med && (med.schedule_type || 'daily') === 'dialysis') continue;
    // "I need a reminder" unticked: not reminded, not listed, never missed.
    if (!wantsReminder(rem, true)) continue;
    const amount = num(String(rem.dose || '').match(/\d+(\.\d+)?/)?.[0]) || 1;
    out.push({
      key: `${rem.id}|${dateISO}|injection`, source: 'injection', sourceId: rem.id, medicineId: med ? med.id : null,
      name: rem.name || (med ? med.name : 'Injection'), strength: med ? String(med.strength || '') + (med.strength ? ' ' + (med.strength_unit || '') : '') : '',
      type: med ? med.type : 'injection', slot: 'injection', date: dateISO, time: rem.time || '09:00',
      amount, amountLabel: rem.dose ? String(rem.dose) : amountLabel(amount, 'injection'), label: SLOT_LABELS.injection,
    });
  }

  const dialysisDay = isDialysisDay(person, dateISO);
  for (const med of meds || []) {
    if (coveredByInjection.has(med.id) || !medicineActiveOn(med, dateISO)) continue;
    if (!wantsReminder(med as { remind?: unknown; type?: unknown })) continue; // an injection with "I need a reminder" unticked
    const base = {
      source: 'medicine' as const, sourceId: med.id, medicineId: med.id, name: med.name,
      strength: med.strength ? `${med.strength} ${med.strength_unit || ''}`.trim() : '', type: med.type, date: dateISO,
    };
    const push = (slot: DoseSlot, time: string, amount: number, label = SLOT_LABELS[slot as keyof typeof SLOT_LABELS]) => {
      if (!(amount > 0)) return;
      out.push({ ...base, key: `${med.id}|${dateISO}|${slot}`, slot, time, amount, amountLabel: amountLabel(amount, med.type), label });
    };
    const type = med.schedule_type || 'daily';
    if (type === 'fixed') continue;
    if (type === 'course') {
      const c = courseInfo(med);
      if (!c) continue;
      for (const x of courseDosesOn(c, dateISO)) {
        out.push({ ...base, key: `${med.id}|${dateISO}|course_${x.n}`, slot: `course_${x.n}`, time: x.time, amount: c.dose,
          amountLabel: amountLabel(c.dose, med.type), label: `Dose ${x.n} of ${c.total}`, course: { n: x.n, total: c.total } });
      }
      continue;
    }
    if (type === 'interval') {
      const start = parseDateISO(med.start_date || '');
      const d = parseDateISO(dateISO)!;
      const every = Math.max(1, parseInt(String(med.interval_days ?? 1), 10) || 1);
      if (!start) continue; // no start date: no way to know which day
      const diff = Math.round((d.getTime() - start.getTime()) / 86400000);
      if (diff >= 0 && diff % every === 0) push('interval', times.morning, num(med.interval_dose), `Every ${every} day${every === 1 ? '' : 's'}`);
      continue;
    }
    if (type === 'dialysis') continue; // given at dialysis: no reminder, not counted (dialysisMedsOn lists them)
    if (med.skip_on_dialysis_day && dialysisDay) continue; // not taken on dialysis days
    for (const slot of MEAL_SLOTS) {
      push(slot, slotTime(slot, times), num((med as Record<string, unknown>)[slot]));
    }
  }
  const start = logStartOf(person);
  return out.filter(d => afterLogStart(start, d.date, d.time)).sort((a, b) => a.time.localeCompare(b.time) || a.name.localeCompare(b.name));
}

// ---------------- log start (set by the family owner) ----------------
/** "Start the phone log from": nothing is planned (reminded, or counted as missed) before this moment. */
export interface LogStart { date: string; time: string }
/** The family's log start carried on the person (see withFamilyTimes), or null. */
export function logStartOf(person: Person | null | undefined): LogStart | null {
  return parseLogStart(person ? (person as { log_start?: unknown }).log_start : null);
}
/** "2026-11-01T08:00" -> { date, time }; anything else -> null. */
export function parseLogStart(v: unknown): LogStart | null {
  const m = typeof v === 'string' ? /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})$/.exec(v) : null;
  return m && parseDateISO(m[1]) ? { date: m[1], time: m[2] } : null;
}
/** Is a planned date + time on or after the log start? */
export function afterLogStart(start: LogStart | null, dateISO: string, hhmm: string): boolean {
  if (!start) return true;
  return dateISO > start.date || (dateISO === start.date && hhmm >= start.time);
}

/** A medicine given at the dialysis centre on a dialysis day (shown for information, never reminded or counted). */
export interface DialysisMed { medicineId: string; name: string; strength: string; type: string; amount: number; amountLabel: string; time: string }
/** "On dialysis days only" medicines for a dialysis day; empty on other days. */
export function dialysisMedsOn(person: Person | null | undefined, meds: Medicine[], dateISO: string): DialysisMed[] {
  if (!isDialysisDay(person, dateISO)) return [];
  const t = (person && person.dialysis_time) || 'morning';
  return (meds || []).filter(m => (m.schedule_type || 'daily') === 'dialysis' && medicineActiveOn(m, dateISO) && num(m.dialysis_per_session) > 0)
    .map(m => ({ medicineId: m.id, name: m.name, strength: m.strength ? `${m.strength} ${m.strength_unit || ''}`.trim() : '', type: m.type,
      amount: num(m.dialysis_per_session), amountLabel: amountLabel(num(m.dialysis_per_session), m.type), time: DIALYSIS_SESSION_TIMES[t] || DIALYSIS_SESSION_TIMES.morning }));
}

/** Doses at the same time become one reminder ("8:00 AM · After breakfast · 3 medicines"). */
export function groupDoses(doses: PlannedDose[]): DoseGroup[] {
  const map = new Map<string, DoseGroup>();
  for (const d of doses) {
    const k = `${d.date} ${d.time}`;
    let g = map.get(k);
    if (!g) { g = { date: d.date, time: d.time, label: d.label, doses: [] }; map.set(k, g); }
    if (!g.doses.some(x => x.label === d.label) && g.doses.length) g.label = [...new Set([...g.label.split(' · '), d.label])].join(' · ');
    g.doses.push(d);
  }
  return [...map.values()].sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time));
}

// ---------------- status of a dose ----------------
export type LoggedStatus = 'taken' | 'skipped' | 'missed';
export type DoseState = LoggedStatus | 'due' | 'later';
export const MISSED_AFTER_HOURS = 4;

/** A local Date for a date + "HH:MM". */
export function doseMoment(dateISO: string, hhmm: string): Date {
  const [y, mo, d] = dateISO.split('-').map(Number);
  const [h, m] = (hhmm || '00:00').split(':').map(Number);
  return new Date(y, mo - 1, d, h || 0, m || 0, 0, 0);
}

/**
 * taken / skipped / missed (from the log), otherwise:
 * later (time not reached), due (time passed, not marked yet),
 * missed (not marked MISSED_AFTER_HOURS after the time).
 */
export function doseState(dose: Pick<PlannedDose, 'date' | 'time'>, logged: LoggedStatus | null | undefined, now = new Date(), missedAfterHours = MISSED_AFTER_HOURS): DoseState {
  if (logged) return logged;
  const t = doseMoment(dose.date, dose.time).getTime();
  if (now.getTime() < t) return 'later';
  if (now.getTime() - t >= missedAfterHours * 3600000) return 'missed';
  return 'due';
}

// ---------------- stock ----------------
/** Average units used per day over the next 4 weeks (same formula as purchasing). */
export function dailyUse(med: Medicine, person?: Person | null, today = new Date()): number {
  const from = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()));
  const to = addDays(from, 27);
  const n = computeRangeNeed(med, from, to, person);
  if (n.status === 'ended') return 0;
  // computeRangeNeed rounds the 4-week total up; that's fine for an estimate.
  return n.needed / 28;
}

/** Whole days the medicine in hand lasts; null when it isn't used regularly. */
export function daysLeft(med: Medicine, person?: Person | null, today = new Date()): number | null {
  const use = dailyUse(med, person, today);
  if (!(use > 0)) return null;
  return Math.floor(num(med.on_hand) / use);
}
export const LOW_STOCK_DAYS = 5;

// ---------------- blood pressure ----------------
export interface BpLimits { high_sys: number; high_dia: number; very_high_sys: number; very_high_dia: number; low_sys: number; low_dia: number }
export const DEFAULT_BP_LIMITS: BpLimits = { high_sys: 140, high_dia: 90, very_high_sys: 180, very_high_dia: 110, low_sys: 90, low_dia: 60 };
export interface BpSchedule { times: string[]; days: number[] /* 0=Sun..6; empty = every day */; limits: BpLimits; enabled: boolean }
export const DEFAULT_BP_SCHEDULE: BpSchedule = { times: ['08:00', '20:00'], days: [], limits: DEFAULT_BP_LIMITS, enabled: true };

export function bpScheduleFor(person: Person | null | undefined): BpSchedule {
  const s = (person && (person as { bp_schedule?: Partial<BpSchedule> }).bp_schedule) || {};
  return {
    enabled: s.enabled !== false,
    times: Array.isArray(s.times) ? s.times.filter(t => /^\d{2}:\d{2}$/.test(t)).sort() : DEFAULT_BP_SCHEDULE.times,
    days: Array.isArray(s.days) ? s.days.map(Number).filter(n => n >= 0 && n <= 6) : [],
    limits: { ...DEFAULT_BP_LIMITS, ...(s.limits || {}) },
  };
}

// ---------------- family reminder times (set by the owner) ----------------
/** Family setting key (family_settings) holding the family's reminder times. */
export const REMINDER_TIMES_KEY = 'mpc_reminder_times_v1';

/** Times the family owner sets for everyone in the family. */
export interface FamilyReminderTimes {
  dose_times: DoseTimes;
  /** "YYYY-MM-DDTHH:MM": the phone log (reminders, Daily log) starts here; null = no start set. */
  log_start: string | null;
  /** limits: the family's blood pressure levels (High / Very high / Low; Normal is in between). */
  bp: { enabled: boolean; times: string[]; days: number[]; limits: BpLimits };
}
export const DEFAULT_FAMILY_TIMES: FamilyReminderTimes = {
  dose_times: DEFAULT_DOSE_TIMES,
  log_start: null,
  bp: { enabled: true, times: DEFAULT_BP_SCHEDULE.times, days: [], limits: DEFAULT_BP_LIMITS },
};

/** Read the family setting (JSON text); anything missing or wrong uses the defaults. */
export function parseFamilyTimes(raw: string | null | undefined): FamilyReminderTimes {
  let v: Partial<FamilyReminderTimes> = {};
  try { v = raw ? JSON.parse(raw) : {}; } catch { v = {}; }
  const dt = doseTimesFor({ id: '', name: '', dose_times: v.dose_times } as Person);
  const bp = bpScheduleFor({ id: '', name: '', bp_schedule: v.bp } as Person);
  const ls = parseLogStart(v.log_start);
  return { dose_times: dt, log_start: ls ? `${ls.date}T${ls.time}` : null, bp: { enabled: bp.enabled, times: bp.times, days: bp.days, limits: bp.limits } };
}

/**
 * The person as reminders see them: medicine times, BP check times and BP
 * limits come from the family (set by the owner). If the owner hasn't saved
 * BP limits yet, a person's older own limits still apply.
 * When the family has no settings saved at all, the person's own (or default) values apply.
 */
export function withFamilyTimes<P extends Person>(person: P, familyRaw: string | null | undefined): P {
  if (!familyRaw) return person;
  const f = parseFamilyTimes(familyRaw);
  let hasLimits = false;
  try { hasLimits = !!(JSON.parse(familyRaw)?.bp?.limits); } catch { /* ignore */ }
  const limits = hasLimits ? f.bp.limits : bpScheduleFor(person).limits;
  return { ...person, dose_times: f.dose_times, bp_schedule: { ...f.bp, limits }, log_start: f.log_start };
}

/** BP checks planned on a date ("HH:MM" list). */
export function bpChecksOn(person: Person | null | undefined, dateISO: string): string[] {
  const s = bpScheduleFor(person);
  if (!s.enabled) return [];
  const d = parseDateISO(dateISO);
  if (s.days.length && d && !s.days.includes(d.getUTCDay())) return [];
  const start = logStartOf(person);
  return s.times.filter(t => afterLogStart(start, dateISO, t));
}

export type BpLevel = 'very_high' | 'high' | 'low' | 'normal';
export function bpLevel(sys: number, dia: number, limits: BpLimits = DEFAULT_BP_LIMITS): BpLevel {
  if (sys >= limits.very_high_sys || dia >= limits.very_high_dia) return 'very_high';
  if (sys >= limits.high_sys || dia >= limits.high_dia) return 'high';
  if (sys <= limits.low_sys || dia <= limits.low_dia) return 'low';
  return 'normal';
}
export const BP_LEVEL_TEXT: Record<BpLevel, { word: string; advice: string }> = {
  normal: { word: 'Normal', advice: 'Good. Keep taking your medicines.' },
  high: { word: 'A bit high', advice: 'Sit quietly for 5 minutes and check again. Tell your doctor at your next visit.' },
  very_high: { word: 'Very high', advice: 'Contact your doctor now. Your carer has been told.' },
  low: { word: 'Low', advice: 'Sit or lie down. If you feel dizzy, call your carer.' },
};

/** Today in the phone's own time zone, "YYYY-MM-DD". */
export function localTodayISO(now = new Date()): string {
  return formatDateISO(new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())));
}
